import {
  useCreateEmployeeView,
  useDeleteEmployeeView,
  useEmployeeViews,
  useReorderEmployeeViews,
  useSetDefaultEmployeeView,
  useUpdateEmployeeView,
} from "@/queries/employees/use-employee-views.query.ts";
import { ColumnDefinition } from "@/types/column.types.ts";
import { ApiEmployeeListView, ApiEmployeeViewType } from "@/types/employees/employee-view.types.ts";
import { ApiEmployeeFilter } from "@/types/employees/employee.types.ts";
import { isEqual } from "lodash";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export const VIEW_PARAM = "view";
export const ALL_EMPLOYEES_PARAM = "all";

// is_default — легаси во value старых вьюх (дефолт теперь top-level); без среза считается фильтром.
const OMITTED_VALUE_KEYS = ["is_default", "page_number", "text"];

const isEmptyValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === "object" &&
    value !== null &&
    "fields" in value &&
    Array.isArray((value as { fields: unknown[] }).fields) &&
    (value as { fields: unknown[] }).fields.length === 0);

const normalizeValue = (value: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(value).filter(
      ([key, val]) => !OMITTED_VALUE_KEYS.includes(key) && !isEmptyValue(val)
    )
  );

const filtersToValue = (filter: ApiEmployeeFilter): Record<string, unknown> =>
  normalizeValue({ ...filter });

const viewColumns = (view?: ApiEmployeeListView): ColumnDefinition[] =>
  view?.columns?.columns ?? [];

const columnIds = (columns: ColumnDefinition[]): string[] => columns.map((column) => column.id);

export const viewFilterCount = (view: ApiEmployeeListView): number =>
  Object.keys(normalizeValue(view.value)).filter((key) => key !== "sorting").length;

export const useEmployeeViewsNav = (enabled = true) => {
  const [searchParams] = useSearchParams();

  const { data: allViews = [], isLoading } = useEmployeeViews(
    ApiEmployeeViewType.employee_list,
    enabled
  );

  const landingDefaultView = useMemo(() => allViews.find((view) => view.is_default), [allViews]);

  const views = useMemo(() => [...allViews].sort((a, b) => a.sort - b.sort), [allViews]);

  const viewIdParam = searchParams.get(VIEW_PARAM);
  const activeView = useMemo(() => {
    if (viewIdParam === ALL_EMPLOYEES_PARAM) return undefined;
    if (viewIdParam) {
      const found = allViews.find((view) => String(view.id) === viewIdParam);
      if (found) return found;
    }
    return landingDefaultView;
  }, [viewIdParam, allViews, landingDefaultView]);

  const isDefaultActive = !activeView;

  const activeViewFilters = useMemo(
    () => (activeView ? (normalizeValue(activeView.value) as ApiEmployeeFilter) : null),
    [activeView]
  );

  return {
    views,
    landingDefaultViewId: landingDefaultView?.id,
    activeView,
    activeViewId: activeView?.id,
    activeViewFilters,
    isDefaultActive,
    isLoading,
  };
};

export const useEmployeeViewActions = ({
  activeView,
  currentFilters,
  currentColumns,
  defaultColumnIds,
  onViewSaved,
  onResetToDefault,
}: {
  activeView?: ApiEmployeeListView;
  currentFilters: ApiEmployeeFilter;
  currentColumns: ColumnDefinition[];
  defaultColumnIds: string[];
  onViewSaved: (view: ApiEmployeeListView) => void;
  onResetToDefault: () => void;
}) => {
  const createViewMutation = useCreateEmployeeView();
  const updateViewMutation = useUpdateEmployeeView();
  const deleteViewMutation = useDeleteEmployeeView();
  const reorderViewsMutation = useReorderEmployeeViews();
  const setDefaultViewMutation = useSetDefaultEmployeeView();

  const isDirty = useMemo(() => {
    const savedFilters = activeView ? normalizeValue(activeView.value) : {};
    const filtersChanged = !isEqual(savedFilters, filtersToValue(currentFilters));
    const savedColumnIds = activeView ? columnIds(viewColumns(activeView)) : defaultColumnIds;
    const columnsChanged = !isEqual(savedColumnIds, columnIds(currentColumns));
    return filtersChanged || columnsChanged;
  }, [activeView, currentFilters, currentColumns, defaultColumnIds]);

  const saveAsNewView = async (title: string) => {
    const view = await createViewMutation.mutateAsync({
      type: ApiEmployeeViewType.employee_list,
      title,
      value: filtersToValue(currentFilters),
      columns: { columns: currentColumns },
    });
    onViewSaved(view);
  };

  const updateActiveView = () => {
    if (!activeView) return;
    updateViewMutation.mutate({
      viewId: activeView.id,
      body: { value: filtersToValue(currentFilters), columns: { columns: currentColumns } },
    });
  };

  const renameView = async (viewId: number, title: string) => {
    await updateViewMutation.mutateAsync({ viewId, body: { title } });
  };

  const deleteView = (viewId: number, options?: { onSuccess?: () => void }) => {
    deleteViewMutation.mutate(
      { viewId, type: ApiEmployeeViewType.employee_list },
      {
        onSuccess: () => {
          if (activeView?.id === viewId) onResetToDefault();
          options?.onSuccess?.();
        },
      }
    );
  };

  const reorderViews = (
    viewIds: number[],
    options?: { onError?: () => void; onSettled?: () => void }
  ) => {
    reorderViewsMutation.mutate(
      { type: ApiEmployeeViewType.employee_list, viewIds },
      { onError: options?.onError, onSettled: options?.onSettled }
    );
  };

  const setDefaultView = useCallback(
    (viewId: number) => {
      setDefaultViewMutation.mutate({ viewId, type: ApiEmployeeViewType.employee_list });
    },
    [setDefaultViewMutation.mutate]
  );

  return {
    isDirty,
    isUpdating: updateViewMutation.isPending,
    saveAsNewView,
    updateActiveView,
    renameView,
    deleteView,
    reorderViews,
    setDefaultView,
  };
};
